/**
 * useT — the translation hook.
 *
 * `const { t, locale } = useT();` then `t('nav.items.dashboard')`. Keys are a
 * TypeScript union of every dot-path in the catalog (`TranslationKey`), so an
 * unknown or mistyped key is a compile error and refactors stay honest. The
 * component re-renders on a language change because it subscribes to the locale
 * store.
 *
 * Resolution: active-locale catalog → French fallback (FALLBACK_LOCALE, when
 * loaded) → English (DEFAULT_LOCALE, always bundled) → the key itself (so a
 * genuinely missing key is visible in the UI rather than blank).
 * `{{var}}` placeholders are interpolated from the optional `vars` argument.
 */

import { useCallback } from 'react';

import { DEFAULT_LOCALE, FALLBACK_LOCALE, type Locale } from './config';
import { catalogs, type TranslationKeys } from './locales';
import { useLocaleStore } from './store';

/**
 * Union of every dot-path whose leaf is a string, derived from the catalog
 * shape — e.g. `'common.save' | 'nav.items.dashboard' | 'profile.prefs.theme'`.
 */
type PathInto<T> = {
  [K in keyof T & string]: T[K] extends string ? K : `${K}.${PathInto<T[K]>}`;
}[keyof T & string];

export type TranslationKey = PathInto<TranslationKeys>;

export type TVars = Record<string, string | number>;

/** Walk a dotted key into a catalog; return the string leaf or undefined. */
function resolve(catalog: TranslationKeys | undefined, key: string): string | undefined {
  if (!catalog) return undefined;
  let cur: unknown = catalog;
  for (const part of key.split('.')) {
    if (cur !== null && typeof cur === 'object' && part in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return typeof cur === 'string' ? cur : undefined;
}

/** Replace `{{name}}` placeholders with values from `vars`. */
export function interpolate(template: string, vars?: TVars): string {
  if (!vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : match,
  );
}

/**
 * Pure translator for a given locale (used by the hook and by tests).
 *
 * Non-default catalogs load lazily (see ./locales): the chain is the active
 * locale → the French source (when loaded) → English (always bundled) → the key
 * itself, so a lookup never blanks out even before/without a lazy chunk.
 */
export function translate(locale: Locale, key: string, vars?: TVars): string {
  const raw =
    resolve(catalogs[locale], key) ??
    resolve(catalogs[FALLBACK_LOCALE], key) ??
    resolve(catalogs[DEFAULT_LOCALE], key) ??
    key;
  return interpolate(raw, vars);
}

export interface UseTranslation {
  t: (key: TranslationKey, vars?: TVars) => string;
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

export function useT(): UseTranslation {
  const locale = useLocaleStore((s) => s.locale);
  const setLocale = useLocaleStore((s) => s.setLocale);

  const t = useCallback(
    (key: TranslationKey, vars?: TVars) => translate(locale, key, vars),
    [locale],
  );

  return { t, locale, setLocale };
}

/**
 * Non-hook translator for copy that lives OUTSIDE a component body (module-level
 * option lists, helper functions, default props). It resolves against the locale
 * active at call time; module-level constants are therefore evaluated in the
 * locale of the page load — which is why a language switch reloads the page
 * (see `switchLocale`), keeping every string consistent.
 */
export function tr(key: TranslationKey, vars?: TVars): string {
  return translate(useLocaleStore.getState().locale, key, vars);
}

/** BCP 47 tag used for `Intl` / `toLocale*String` formatting in each UI locale. */
const INTL_TAGS: Record<Locale, string> = {
  fr: 'fr-CA',
  en: 'en-US',
  // Arabic copy with Latin digits (matches the numbers typed and shown elsewhere).
  ar: 'ar-u-nu-latn',
};

/**
 * Locale tag for number / date formatting in the ACTIVE UI language. In French
 * it returns the call site's own regional tag (`fr-FR` vs `fr-CA` formats
 * differ), so French output is unchanged; in English / Arabic it returns that
 * language's tag — no more French-formatted dates in an English UI.
 */
export function intlLocale(frenchTag: string = INTL_TAGS.fr): string {
  const locale = useLocaleStore.getState().locale;
  return locale === 'fr' ? frenchTag : INTL_TAGS[locale];
}

/** Alias so the call site reads like the familiar react-i18next hook. */
export const useTranslation = useT;
